/**
 * Receiving several of one supplier's purchase orders in ONE handheld session (D65).
 *
 * Resolute Wood alone had 70 open orders on 2026-10-03, and the same item sits on as many as 18 of
 * them. Receiving one order at a time meant one scan session per order. This pushes one order for
 * all the ticked ones; the scan is split back into a receipt per purchase order, oldest first.
 *
 *   ONE SUPPLIER — a goods received note carries a single supplier code, so a session spanning two
 *     could never post. Only suppliers with more than one open order are offered.
 *   PREVIEW IS REAL — the order reaches a handheld at once and is locked to us after the first
 *     scan (D28). Send stays disabled until a preview exists for exactly the ticked selection.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api, qty } from '../lib/api';
import { Table, Loading, Empty, cell } from '../components/ui';

export type OpenPo = {
  docno: string;
  docdate: string;
  supplierCode: string | null;
  supplierName: string | null;
};

type MergedView = {
  supplierName: string | null;
  purchaseOrders: Array<{ docno: string; docdate: string; lines: number; units: string }>;
};

type MergedPreview = {
  orderNo: string;
  payload?: { planQty: number; data: Array<{ sku: string; planQty: number }> };
  excluded?: Array<{ line: { itemcode: string }; reason: string }>;
  dryRun?: boolean;
  simulated?: boolean;
};

export function MergedReceive({ purchaseOrders }: { purchaseOrders: OpenPo[] }): JSX.Element {
  const [supplier, setSupplier] = useState('');
  const [ticked, setTicked] = useState<string[]>([]);
  const [deliveryNote, setDeliveryNote] = useState('');
  const [preview, setPreview] = useState<MergedPreview | null>(null);
  const [pushed, setPushed] = useState<MergedPreview | null>(null);

  // Suppliers with more than one open order — merging a single order is just receiving it.
  const bySupplier = new Map<string, { name: string; count: number }>();
  for (const p of purchaseOrders) {
    if (!p.supplierCode) continue;
    const cur = bySupplier.get(p.supplierCode) ?? {
      name: p.supplierName ?? p.supplierCode,
      count: 0,
    };
    cur.count += 1;
    bySupplier.set(p.supplierCode, cur);
  }
  const suppliers = [...bySupplier.entries()]
    .filter(([, s]) => s.count > 1)
    .sort((a, b) => a[1].name.localeCompare(b[1].name));

  const view = useQuery({
    queryKey: ['receive', 'merged', supplier],
    enabled: supplier !== '',
    queryFn: () =>
      api.get<MergedView>(`/api/v1/rfid/receive/merged?supplier=${encodeURIComponent(supplier)}`),
  });

  // Anything that changes what would be sent throws the preview away: the order sent must be the
  // order shown.
  const reset = () => {
    setPreview(null);
    setPushed(null);
  };

  // Oldest first, as the list is ordered — which is also the order a scan fills them in.
  const docnos = (view.data?.purchaseOrders ?? [])
    .map((p) => p.docno)
    .filter((d) => ticked.includes(d));

  const send = useMutation({
    mutationFn: (confirm: boolean) =>
      api.post<MergedPreview>('/api/v1/rfid/receive/merged', {
        supplier,
        docnos,
        ...(deliveryNote.trim() ? { deliveryNote: deliveryNote.trim() } : {}),
        ...(confirm ? { confirm: true } : {}),
      }),
    onSuccess: (r, confirm) => (confirm ? setPushed(r) : setPreview(r)),
  });

  const units = preview?.payload?.planQty ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">Supplier</span>
          <select
            value={supplier}
            onChange={(e) => {
              setSupplier(e.target.value);
              setTicked([]);
              reset();
            }}
            aria-label="Supplier"
            className="w-96 rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none"
          >
            <option value="">Choose a supplier…</option>
            {suppliers.map(([code, s]) => (
              <option key={code} value={code}>
                {s.name} ({code}) · {s.count} POs
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">Delivery note no. (optional)</span>
          <input
            value={deliveryNote}
            onChange={(e) => {
              setDeliveryNote(e.target.value);
              reset();
            }}
            aria-label="Delivery note number"
            placeholder="from the supplier's DO"
            className="w-56 rounded border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
          />
        </label>
      </div>

      {supplier !== '' && view.isLoading && <Loading />}
      {view.isError && (
        <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
          {(view.error as Error).message}
        </p>
      )}

      {view.data &&
        (view.data.purchaseOrders.length === 0 ? (
          <Empty>Nothing outstanding for this supplier.</Empty>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              Scanned pieces fill the <strong>oldest</strong> ticked purchase order first, up to
              what each line is owed. Anything left over is reported, never forced onto an order.
            </p>
            <Table
              head={['', 'PO', 'Date', 'Lines', 'Units due']}
              rows={view.data.purchaseOrders.map((p) => [
                cell(
                  ticked.includes(p.docno) ? 1 : 0,
                  <input
                    type="checkbox"
                    checked={ticked.includes(p.docno)}
                    onChange={(e) => {
                      setTicked((t) =>
                        e.target.checked ? [...t, p.docno] : t.filter((d) => d !== p.docno),
                      );
                      reset();
                    }}
                    aria-label={`Include ${p.docno}`}
                  />,
                ),
                <span className="font-mono text-xs font-semibold">{p.docno}</span>,
                <span className="text-slate-600">{String(p.docdate).slice(0, 10)}</span>,
                cell(p.lines, <span>{p.lines}</span>),
                cell(Number(p.units), <span>{qty(p.units)}</span>),
              ])}
            />

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-slate-500">{docnos.length} PO(s) ticked</span>
              <button
                type="button"
                disabled={docnos.length < 2 || send.isPending}
                onClick={() => send.mutate(false)}
                className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {send.isPending && send.variables === false ? 'Checking…' : 'Preview the order'}
              </button>
              <button
                type="button"
                disabled={!preview || !!pushed || send.isPending}
                onClick={() => send.mutate(true)}
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
              >
                {send.isPending && send.variables === true
                  ? 'Sending…'
                  : preview
                    ? `Send to handheld (${docnos.length} POs, ${units} pieces)`
                    : 'Send to handheld'}
              </button>
              {docnos.length < 2 && (
                <span className="text-xs text-slate-500">tick at least two orders</span>
              )}
            </div>
          </>
        ))}

      {send.isError && (
        <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
          {(send.error as Error).message}
        </p>
      )}

      {preview && !pushed && (
        <div className="space-y-2 rounded border border-slate-200 bg-brand-50 px-4 py-3 text-sm">
          <p className="text-slate-700">
            One handheld order <strong className="font-mono">{preview.orderNo}</strong> for{' '}
            {docnos.length} purchase orders: <strong>{preview.payload?.data.length ?? 0}</strong>{' '}
            plan line(s), <strong>{units}</strong> piece(s) to scan.
            {preview.dryRun && (
              <span className="text-slate-500"> DRY_RUN is on — nothing will be sent.</span>
            )}
          </p>
          <Table
            head={['Scan code', 'Pieces']}
            rows={(preview.payload?.data ?? []).map((d) => [
              <span className="font-mono text-xs">{d.sku}</span>,
              cell(d.planQty, <span>{d.planQty}</span>),
            ])}
          />
          {(preview.excluded?.length ?? 0) > 0 && (
            <p className="text-xs text-slate-600">
              Left off:{' '}
              {preview.excluded?.map((e) => `${e.line.itemcode} (${e.reason})`).join('; ')}
            </p>
          )}
        </div>
      )}

      {pushed && (
        <p className="rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {pushed.simulated ? (
            <>Simulated — DRY_RUN is on, nothing was sent.</>
          ) : (
            <>
              Sent as <strong className="font-mono">{pushed.orderNo}</strong>. It should appear on
              the handheld now; when it is submitted, a receipt is posted for each purchase order.
            </>
          )}
        </p>
      )}
    </div>
  );
}
